import { useCallback, useMemo, useState } from 'react';

import { Flex, Modal } from 'antd';
import { MInput } from '@Components/Base';
import { MobileDrawer } from '@Components/index';
import { ViewResolver } from '@Components/ViewResolver';

import { useAppSelector } from '@Hooks/index';
import { getModalRecordKey } from '@Store/selectors/modalSelector';
import { TenderVendorListResponse } from '@Types/api/form/tenderRequest/TenderVendorListResponse';
import styles from './VendorsListModal.module.css';
import { InfoCard } from '../InfoCard';
import { useGetVendorListQuery } from '../../TenderRequest/store/api/TenderRequestApiSlice';
import { findTenderVendorsBySearchValue } from '../../utils/finder';

interface VendorsListModalProps {
  isOpen: boolean;
  onCancel: () => void;
}

const VendorsListModal: React.FC<VendorsListModalProps> = ({
  isOpen,
  onCancel,
}) => {
  const [searchValue, setSearchValue] = useState<string>();
  const [serchVendors, setSearchVendors] = useState<TenderVendorListResponse>();
  const tenderId = useAppSelector(getModalRecordKey);
  const { data } = useGetVendorListQuery(tenderId, {
    skip: !tenderId || !isOpen,
    refetchOnMountOrArgChange: true,
  });
  const handleSearchVendors = useCallback(
    (value: string) => {
      setSearchValue(value);

      if (data) {
        const result = findTenderVendorsBySearchValue(data, value);
        setSearchVendors(result);
      }
    },
    [data]
  );
  const dataToDisplay = useMemo(() => {
    if (searchValue) {
      return serchVendors;
    }
    return data;
  }, [data, searchValue, serchVendors]);

  const content = useMemo(
    () => (
      <Flex className={styles.main} vertical gap={16}>
        <MInput
          searchInput
          value={searchValue}
          placeholder="Search..."
          onChange={(event) => handleSearchVendors(event.target.value)}
        />
        <Flex className={styles.container} vertical gap={16}>
          {dataToDisplay?.map((item) => (
            <InfoCard
              key={item.vendorId}
              title={item.name}
              items={[
                { label: 'Vendor ID', value: String(item.vendorId) },
                { label: 'Email', value: item.email },
                { label: 'Phone Number', value: item.tel },
              ]}
            />
          ))}
        </Flex>
      </Flex>
    ),
    [dataToDisplay, handleSearchVendors, searchValue]
  );

  return (
    <ViewResolver
      desktop={
        <Modal
          open={isOpen}
          className={styles.modal}
          footer={null}
          onCancel={onCancel}
          width={600}
          title={`List of Vendors - Tender No. ${tenderId}`}
        >
          {content}
        </Modal>
      }
      mobile={
        <MobileDrawer
          title={`List of Vendors - Tender No. ${tenderId}`}
          onClose={onCancel}
          open={isOpen}
        >
          {content}
        </MobileDrawer>
      }
    />
  );
};

export default VendorsListModal;
